import React, { useState, useEffect } from 'react';
import { clientPortalApi } from '../../lib/http';

interface FavoriteToggleProps {
  expertSlug: string;
  isFavorite: boolean;
  onToggle?: (isFavorite: boolean) => void;
}

const FavoriteToggle: React.FC<FavoriteToggleProps> = ({
  expertSlug,
  isFavorite: initialIsFavorite,
  onToggle,
}) => {
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isLoading, setIsLoading] = useState(false);

  // Sync internal state when prop changes
  useEffect(() => {
    setIsFavorite(initialIsFavorite);
  }, [initialIsFavorite]);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const previousState = isFavorite;
    const newState = !isFavorite;

    // Optimistic update
    setIsFavorite(newState);
    setIsLoading(true);

    try {
      if (newState) {
        await clientPortalApi.favorites.add(expertSlug);
      } else {
        await clientPortalApi.favorites.remove(expertSlug);
      }

      onToggle?.(newState);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Rollback on error
      setIsFavorite(previousState);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`p-2 rounded-lg transition-all ${
        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
      }`}
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorite}
    >
      {isFavorite ? (
        // Filled heart
        <svg
          className="w-6 h-6 text-purple-600"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        // Outline heart
        <svg
          className="w-6 h-6 text-gray-400 hover:text-purple-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      )}
    </button>
  );
};

export default FavoriteToggle;
